/**
 * TemplatesPage Component
 * AIDLC Administrative Backend
 *
 * Module 07: Standard Operating Procedures
 * SOP library for reusable agent workflows.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import {
  getAllTemplates,
  createTemplate,
  type TaskTemplate,
  type TemplateStep,
} from '../../services/templateService';

export function TemplatesPage() {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newEstimatedDuration, setNewEstimatedDuration] = useState('');
  const [newSteps, setNewSteps] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await getAllTemplates();
        setTemplates(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load SOPs');
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(templates.map((t) => t.category));
    return Array.from(cats).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (filterCategory !== 'all') {
      result = result.filter((t) => t.category === filterCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [templates, filterCategory, searchQuery]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      const steps: TemplateStep[] = newSteps
        .split('\n')
        .filter((s) => s.trim())
        .map((s, idx) => ({
          order: idx + 1,
          agentId: '',
          action: s.trim(),
          description: s.trim(),
          gate: null,
        }));

      const created = await createTemplate({
        name: newName,
        description: newDescription,
        category: newCategory || 'General',
        agentIds: [],
        steps,
        estimatedDuration: newEstimatedDuration || '1 session',
        isActive: true,
        createdBy: 'Neo',
      });
      setTemplates((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewName('');
      setNewDescription('');
      setNewCategory('');
      setNewEstimatedDuration('');
      setNewSteps('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create SOP');
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading SOPs..." />;

  return (
    <div>
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-black">{templates.length}</div>
          <div className="text-xs text-gray-500 mt-1">Total SOPs</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-blue">{categories.length}</div>
          <div className="text-xs text-gray-500 mt-1">Categories</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {templates.filter((t) => t.isActive).length}
          </div>
          <div className="text-xs text-gray-500 mt-1">Active</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search SOPs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
          />
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setFilterCategory('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filterCategory === 'all'
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filterCategory === cat
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)} className="ml-auto">
          + New SOP
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* SOP Grid */}
      {filteredTemplates.length === 0 ? (
        <EmptyState
          title="No SOPs found"
          description="Create reusable standard operating procedures for common agent workflows."
          action={
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
              Create First SOP
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-ldaja-blue/30 transition-all cursor-pointer"
              onClick={() => setSelectedTemplate(template)}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold text-ldaja-black">{template.name}</h3>
                <Badge variant="info">{template.category}</Badge>
              </div>
              <p className="text-xs text-gray-500 mb-3 line-clamp-2">{template.description}</p>

              {/* Steps Preview */}
              <div className="mb-3">
                {(template.steps || []).slice(0, 3).map((step, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-[10px] text-gray-500 mb-0.5">
                    <span className="text-gray-400 font-mono w-3 flex-shrink-0">{step.order || idx + 1}.</span>
                    <span className="truncate">{step.action || step.description}</span>
                  </div>
                ))}
                {(template.steps || []).length > 3 && (
                  <p className="text-[10px] text-gray-400 ml-5">+{template.steps.length - 3} more steps</p>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-[10px] text-gray-400">
                <span>{(template.steps || []).length} steps</span>
                <span>{template.estimatedDuration}</span>
                <Badge variant={template.isActive ? 'success' : 'neutral'} dot>{template.isActive ? 'Active' : 'Inactive'}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SOP Detail Modal */}
      <Modal
        isOpen={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        title={selectedTemplate?.name || 'SOP Details'}
        size="md"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="info">{selectedTemplate.category}</Badge>
              <Badge variant={selectedTemplate.isActive ? 'success' : 'neutral'} dot>
                {selectedTemplate.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <p className="text-sm text-gray-600">{selectedTemplate.description}</p>

            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Steps</h4>
              <ol className="space-y-2">
                {(selectedTemplate.steps || []).map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 bg-ldaja-blue/10 text-ldaja-blue rounded-full flex items-center justify-center text-[10px] font-bold">
                      {step.order || idx + 1}
                    </span>
                    <div className="pt-0.5">
                      <span className="text-xs text-gray-600">{step.action}</span>
                      {step.description && step.description !== step.action && (
                        <p className="text-[10px] text-gray-400 mt-0.5">{step.description}</p>
                      )}
                      {step.gate && (
                        <p className="text-[10px] text-amber-600 mt-0.5">Gate: {step.gate}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-100">
              <div>
                <p className="text-[10px] text-gray-400">Duration</p>
                <p className="text-xs font-medium text-gray-700">{selectedTemplate.estimatedDuration}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Agents</p>
                <p className="text-xs font-medium text-gray-700">{(selectedTemplate.agentIds || []).length}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Created By</p>
                <p className="text-xs font-medium text-gray-700">{selectedTemplate.createdBy}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create SOP Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New SOP"
        size="md"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              isLoading={isCreating}
              disabled={!newName.trim()}
            >
              Create SOP
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">SOP Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="e.g., Code Review Workflow"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="What does this SOP automate?"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                placeholder="e.g., Development"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Est. Duration</label>
              <input
                type="text"
                value={newEstimatedDuration}
                onChange={(e) => setNewEstimatedDuration(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                placeholder="e.g., 2 sessions"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Steps (one per line)</label>
            <textarea
              value={newSteps}
              onChange={(e) => setNewSteps(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 font-mono"
              placeholder="Read the requirements document&#10;Analyze existing code&#10;Write implementation&#10;Write tests&#10;Submit for review"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
